import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  SidePanel,
  ButtonPrimary,
  ButtonSecondary,
  SidePanelType,
  ajvResolver,
  responseErrorsResolver,
  ResponseError,
  Popup,
  PermissionDenied,
  PermissionEnum,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import {
  CorporateFormValues,
  NewApplicationListItem,
} from '../new-application/types';
import { defaultFormValues, changePanelState } from './store/slices/create';
import { setHintVisible } from './store/slices/list';
import CreateForm from '../../components/corporate/create/create';
import { useLazyGetCustomersInfoDictionariesQuery } from '../../store/dictionaries/services';
import { useCreateCorporateApplicationMutation } from '../new-application/store/services';
import { ApplicationSchemeType } from '../customers/types';

import ROUTES from '../../routes';

import cn from './corporate.module.css';

const schema = {
  type: 'object',
  properties: {
    countryOfIncorporationId: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Country Of Incorporation is required',
    },
    companyName: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Company Name is required',
    },
    deliveryChannelId: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Delivery Channel is required',
    },
  },
  required: ['countryOfIncorporationId', 'companyName', 'deliveryChannelId'],
};

const IndividualCustomerCreate = (): React.JSX.Element => {
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);
  const [globalError, setGlobalError] = useState('');

  const {
    corporateCustomers: {
      create: { panelIsOpen, submitSuccess },
    },
    customersDictionaries: { activePermissions, applicationSchemes },
  } = useAppSelector((state) => state);

  const [getInfoDictionaries] = useLazyGetCustomersInfoDictionariesQuery();

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    setError,
  } = useForm<CorporateFormValues>({
    defaultValues: defaultFormValues,
    resolver: ajvResolver(schema),
  });

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { search } = useLocation();

  const handleClose = () => {
    dispatch(changePanelState(false));

    reset(defaultFormValues);

    setTimeout(() => {
      navigate(`${ROUTES.CUSTOMERS_CORPORATE_LIST}${search || ''}`);
    }, 100);
  };

  const [create, { isLoading: loading }] =
    useCreateCorporateApplicationMutation();

  const onSubmit = handleSubmit(async (values) => {
    setGlobalError('');

    const body: CorporateFormValues = {
      countryOfIncorporationId: values.countryOfIncorporationId,
      companyName: values.companyName,
      deliveryChannelId: values.deliveryChannelId,
    };

    if (values.applicationSchemeId) {
      body.applicationSchemeId = values.applicationSchemeId;
    }

    const response = (await create(body)) as {
      data?: { data: NewApplicationListItem };
      error?: ResponseError;
    };

    if (response.error) {
      responseErrorsResolver({
        errors: response.error,
        setError,
        setGlobalError,
      });
    }
  });

  useEffect(() => {
    if (submitSuccess) {
      dispatch(changePanelState(false));

      setTimeout(() => {
        dispatch(setHintVisible(true));
      }, 100);
    }
  }, [dispatch, submitSuccess]);

  useEffect(() => {
    dispatch(changePanelState(true));
  }, []);

  useEffect(() => {
    if (
      activePermissions?.includes(
        PermissionEnum.CORPORATE_CUSTOMERS_CREATE_ACTION,
      )
    ) {
      getInfoDictionaries();
    }
  }, [activePermissions]);

  let content;

  if (
    !activePermissions?.includes(
      PermissionEnum.CORPORATE_CUSTOMERS_CREATE_ACTION,
    )
  ) {
    content = <PermissionDenied />;
  } else {
    content = (
      <CreateForm
        control={control}
        errors={errors}
        applicationSchemes={applicationSchemes?.filter(
          (item) => item.type === ApplicationSchemeType.CORPORATE,
        )}
        activePermissions={activePermissions}
      />
    );
  }

  return (
    <>
      <SidePanel
        type={SidePanelType.Customers}
        closeOnClickOutside
        isOpen={panelIsOpen}
        onClose={isDirty ? () => setDirtyPopupVisible(true) : handleClose}
        title="Create corporate customer"
        errorSidepanel={globalError}
        sidepanelExpandedKey="corporate-create"
        footer={
          <>
            {activePermissions?.includes(
              PermissionEnum.CORPORATE_CUSTOMERS_CREATE_ACTION,
            ) && (
              <ButtonPrimary
                text={loading ? 'Loading...' : 'Create application'}
                onClick={onSubmit}
                bold
                disabled={loading}
              />
            )}
            <ButtonSecondary text="Cancel" onClick={handleClose} bold />
            {Object.keys(errors)?.length > 0 && (
              <span className={cn.formError}>
                There are errors in some fields
              </span>
            )}
          </>
        }
      >
        {content}
      </SidePanel>
      <Popup
        title="You have unsaved changes that will be lost"
        description="Are you sure you want to close this form?"
        onCancelClick={() => setDirtyPopupVisible(false)}
        onOkClick={handleClose}
        cancelButtonText="Cancel"
        okButtonText="Close"
        isVisible={dirtyPopupVisible}
        parent={document.getElementById('dirty-popup') as HTMLElement}
      />
      <div id="dirty-popup" />
    </>
  );
};

export default IndividualCustomerCreate;
